// Every change to a car's settings, newest first, by day: where it was made (on the web, or by which device), what it
// changed in the car's units, and Restore, which makes an earlier revision the newest, after asking.
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { METRIC, unitsOf, type components, type Units } from '@tune/shared';
import { useState } from 'react';
import { ApiError, data, problem } from '../../api';
import { useAuth } from '../../auth';
import { clockText, dayText, deviceRefName } from '../../lib/words';
import { keys, useDevices, useSettings } from '../../queries';
import {
  Badge,
  Banner,
  Button,
  EmptyState,
  ErrorBanner,
  ListSection,
  Skeleton,
  useConfirm,
  useNow,
  useToast,
} from '../../ui';
import { ClockCounterClockwiseIcon } from '../../ui/icons';
import { CarPage } from './CarPage';
import { ALL, describe, fieldOf, historyLabel } from './settings-fields';
import s from './SettingsHistory.module.css';

type Vehicle = components['schemas']['Vehicle'];
type Revision = components['schemas']['SettingsRevision'];
type Device = components['schemas']['Device'];

const PAGE = 20;

/** The changes a revision lists before "Show all". */
const FEW = 5;

export function SettingsHistory() {
  return (
    <CarPage section="Settings history" back={(car) => ({ to: `/garage/${car.id}/settings`, label: 'Settings' })}>
      {(car) => <History car={car} />}
    </CarPage>
  );
}

/** "Today", "Yesterday", or the day. */
function dayHeading(iso: string, now: number): string {
  const d = new Date(iso).toDateString();
  if (d === new Date(now).toDateString()) {
    return 'Today';
  }
  if (d === new Date(now - 86_400_000).toDateString()) {
    return 'Yesterday';
  }
  return dayText(iso, true);
}

/** Who made a change: the web, or a device by the name the owner knows it by. */
function madeBy(r: Revision, devices: readonly Device[]): string {
  return r.by.kind === 'web' ? 'On the web' : `From ${deviceRefName(r.by.device, devices)}`;
}

function History({ car }: { car: Vehicle }) {
  const api = useAuth().api('owners');
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const say = useToast();
  const now = useNow(60_000);
  const settings = useSettings(car.id);
  const devices = useDevices();
  const units = settings.data ? unitsOf(settings.data.values) : METRIC;
  const history = useInfiniteQuery({
    queryKey: keys.history(car.id),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      data(
        api.GET('/vehicles/{vehicleId}/settings/history', {
          params: { path: { vehicleId: car.id }, query: { limit: PAGE, ...(pageParam ? { cursor: pageParam } : {}) } },
        }),
      ),
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const restore = useMutation({
    mutationFn: ({ rev, current }: { rev: number; current: number }) =>
      data(
        api.POST('/vehicles/{vehicleId}/settings/restore', {
          params: { path: { vehicleId: car.id }, header: { 'If-Match': `"${current}"` } },
          body: { rev },
        }),
      ),
    onSuccess: (answer) => {
      queryClient.setQueryData(keys.settings(car.id), answer);
      void queryClient.invalidateQueries({ queryKey: keys.history(car.id) });
      say('Restored. The MFD takes them once the car is stopped.');
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'changed') {
        void queryClient.invalidateQueries({ queryKey: keys.settings(car.id) });
        void queryClient.invalidateQueries({ queryKey: keys.history(car.id) });
      }
    },
  });
  const ask = async (r: Revision) => {
    const current = settings.data?.rev;
    if (current === undefined) {
      return;
    }
    const yes = await confirm({
      title: 'Restore these settings?',
      message: `The settings go back to how they were after the change at ${clockText(r.at)} on ${dayText(r.at)}. The MFD takes them once the car is stopped.`,
      confirm: 'Restore',
      destructive: false,
    });
    if (yes) {
      restore.mutate({ rev: r.rev, current });
    }
  };

  const revisions = history.data?.pages.flatMap((p) => p.revisions) ?? [];
  const days: Array<{ day: string; revisions: Revision[] }> = [];
  for (const r of revisions) {
    const day = dayHeading(r.at, now);
    const last = days.at(-1);
    if (last?.day === day) {
      last.revisions.push(r);
    } else {
      days.push({ day, revisions: [r] });
    }
  }

  return (
    <>
      <p className={s.lead}>Every change to these settings, newest first. Restoring one makes it the newest.</p>
      {restore.isError && (
        <Banner tone="error">
          {restore.error instanceof ApiError && restore.error.code === 'changed'
            ? 'The settings changed meanwhile. Look again, then restore.'
            : problem(restore.error)}
        </Banner>
      )}
      {history.isPending && <Skeleton blocks={2} lines={0} label="Loading the history" />}
      {history.isError && <ErrorBanner retry={() => void history.refetch()}>The history couldn't load.</ErrorBanner>}
      {history.isSuccess && revisions.length === 0 && (
        <EmptyState icon={<ClockCounterClockwiseIcon size={56} />} title="No changes yet">
          Each change to the settings, on the web or on the MFD, shows here.
        </EmptyState>
      )}
      {days.map(({ day, revisions: these }) => (
        <ListSection key={day} header={day}>
          {these.map((r) => (
            <RevisionRow
              key={r.rev}
              revision={r}
              by={madeBy(r, devices.data?.devices ?? [])}
              units={units}
              newest={r.rev === settings.data?.rev}
              busy={restore.isPending}
              onRestore={() => void ask(r)}
            />
          ))}
        </ListSection>
      ))}
      {history.hasNextPage && (
        <div>
          <Button variant="gray" busy={history.isFetchingNextPage} onClick={() => void history.fetchNextPage()}>
            Show older changes
          </Button>
        </div>
      )}
    </>
  );
}

function RevisionRow({
  revision,
  by,
  units,
  newest,
  busy,
  onRestore,
}: {
  revision: Revision;
  by: string;
  units: Units;
  newest: boolean;
  busy: boolean;
  onRestore: () => void;
}) {
  const changes = Object.entries(revision.values);
  // In the menu's order; settings this page doesn't know (from a newer MFD) are counted.
  const known = changes
    .flatMap(([key, value]) => {
      const field = fieldOf(key);
      return field ? [{ key, value, field, at: ALL.indexOf(field) }] : [];
    })
    .sort((a, b) => a.at - b.at);
  const others = changes.length - known.length;
  // A first sync changes every setting: five, and the rest on asking.
  const [all, setAll] = useState(false);
  const listed = all ? known : known.slice(0, FEW);
  return (
    <div className={s.revision}>
      <div className={s.head}>
        <span className={s.when}>
          {clockText(revision.at)} · {by}
        </span>
        {newest ? (
          <Badge tone="neutral">Now</Badge>
        ) : (
          <Button variant="plain" size="small" disabled={busy} onClick={onRestore}>
            Restore
          </Button>
        )}
      </div>
      {revision.restoredFrom !== null && <p className={s.note}>Put back the settings of an earlier change.</p>}
      {known.length > 0 && (
        <ul className={s.changes}>
          {listed.map(({ key, value, field }) => (
            <li key={key}>
              <span className={s.label}>{historyLabel(key)}:</span> {describe(field, value, units)}
            </li>
          ))}
        </ul>
      )}
      {known.length > FEW && (
        <div>
          <Button variant="plain" size="small" aria-expanded={all} onClick={() => setAll((was) => !was)}>
            {all ? 'Show fewer' : `Show all ${known.length} changes`}
          </Button>
        </div>
      )}
      {others > 0 && (
        <p className={s.note}>{others === 1 ? 'And 1 setting' : `And ${others} settings`} only newer MFDs show.</p>
      )}
    </div>
  );
}
